/**
 * Data upload: any file -> our own template workbook (`POST data-upload/convert`).
 *
 * A school uploads its data in whatever shape it has (PDF, Word, a photo of a register, a
 * spreadsheet in its own layout). The backend reads it and hands back OUR template, filled in;
 * the portal then feeds that workbook to the parser it already uses for a hand-filled template.
 * Nothing here saves data. AI servers only: on other servers the routes answer 404.
 *
 * Everything in this file is pure (no React, no redux) so it can be unit tested; the
 * components own dispatching and state.
 */
import type {TranslationKey} from '_metronic/i18n/translationKeys'

// --- Types ------------------------------------------------------------------------------

export type DataUploadUseCaseKey = 'teachers' | 'students' | 'allocation'
export type DataUploadFlavour = 'onboarding' | 'institute'

/** The workbook returned by a conversion, base64 encoded. */
export interface DataUploadTemplate {
  key?: string
  fileName: string
  contentType: string
  base64: string
}

/** A row left OUT of the template, with the reason. */
export interface DataUploadSkippedRow {
  recordId?: string
  label?: string
  reason?: string
  /** The row's own error under `reason`, e.g. `NAME_NOT_ENGLISH` under `INVALID`. */
  code?: string
  message?: string
  /**
   * An allocation line held back for a teacher the school has not saved: that teacher's
   * `newPeople.teachers[].key`, so the line can be shown under them.
   */
  newTeacherKey?: string
  /**
   * A student the school has saved already (a re-upload; `reason: ALREADY_EXISTS`): not written
   * again, and not "left out" either — said as "already in your list".
   */
  alreadyInList?: boolean
  existingStudentId?: number | string
  /**
   * The file the row came from, when several were read together (a row two files share names
   * the first). Absent from older servers.
   */
  fileName?: string
}

/** A skipped row that is a saved student, not a row that could not be used. */
export const isAlreadyInListRow = (row: DataUploadSkippedRow): boolean => row.alreadyInList === true

/**
 * How many of an upload's rows the school already has (`counts.alreadyInList`, else the skipped
 * rows marked so), and the rows really left out: `report.skipped` less those.
 */
export const alreadyInListSplit = (conversion: {
  counts?: DataUploadCounts | null
  report: {skipped: DataUploadSkippedRow[]}
}): {alreadyInList: number; leftOut: DataUploadSkippedRow[]} => {
  const leftOut = conversion.report.skipped.filter((row) => !isAlreadyInListRow(row))
  const marked = conversion.report.skipped.length - leftOut.length
  return {alreadyInList: conversion.counts?.alreadyInList ?? marked, leftOut}
}

/** A row that was written, but is worth a second look. */
export interface DataUploadReviewRow {
  recordId?: string
  label?: string
  fields?: string[]
  /** Fields left blank because the server could not fill them (`class`, `registrationNumber`). */
  missing?: string[]
  /** Candidates for a blank field, e.g. two curricula that both have "Grade 1-A". */
  choices?: Record<string, string[]>
  /**
   * Why each listed field is worth a look, keyed by field name — e.g.
   * `{class: '"Grade 3" is not one of this school's classes'}`. A photo used to cap every
   * value's confidence, so every field of every row was listed with no reason given; now only
   * fields that are missing or were read badly appear, each with its own sentence.
   */
  reasons?: Record<string, string>
  /**
   * Candidates for a field, addressed by id (`options.class[].classId`). Supersedes `choices`,
   * which only names them and is still sent for older builds.
   */
  options?: {class?: DataUploadClassOption[]}
  /** Each field as the file wrote it (`{class: "Grade 6 B"}`). */
  written?: Record<string, string>
}

/**
 * One class a written class name may mean. `displayName` is what to show; `name` is the stored
 * name and is never shown or matched on — `classId` is the class.
 */
export interface DataUploadClassOption {
  classId: number
  name: string
  displayName: string
  /**
   * The class as the school reads it: the plain name, or "<board short code> <name>" only when
   * another board has a class of that name ("Cambridge Grade 7-A"). The Teacher Allocation grid's
   * label; shown as it is, with no " · board" suffix. Absent from older servers.
   */
  schoolDisplayName?: string
  levelId: number | null
  level: string
  curriculumKey: string | null
  curriculum: string
  curriculumShortCode: string
  branchId: number | null
  branch: string
}

/** The class an uploaded row was resolved to, by id (`report.references[]`). */
export interface DataUploadReference {
  recordId: string
  label: string
  registrationNumber: string
  classId: number | null
  levelId: number | null
  branchId: number | null
  subjectIds: number[]
}

/**
 * A class name the file wrote that matches more than one class (two boards, two branches),
 * asked once for every row that wrote it (`report.classQuestions[]`).
 */
export interface DataUploadClassQuestion {
  field: string
  /** Exactly as the file had it. */
  written: string
  /** Every row that wrote it, parallel to `recordIds`. */
  labels: string[]
  recordIds: string[]
  options: DataUploadClassOption[]
}

/**
 * The numbers behind a response's plain `message`: rows read, rows kept, and how many were left
 * out (or need a value) per code.
 */
export interface DataUploadCounts {
  read: number | null
  kept: number | null
  /**
   * How many of the kept rows can actually be saved. `kept` counts the rows that went into the
   * template, which is why the summary used to say "5 students are ready" directly above "4 need
   * a class"; `ready` is the number that sentence means.
   */
  ready: number | null
  leftOut: Record<string, number>
  needValues: Record<string, number>
  /**
   * Every row the file holds is saved in the school already: how many (the summary then says
   * "All N students in this file are already in your school."). Null otherwise.
   */
  alreadyInSchool?: number | null
  /** People the file names that the school has not saved (the portal says so, not `message`). */
  newPeople?: {teachers: number; students: number} | null
  /**
   * A re-upload (AI server): kept rows the school already has — never added again — and the
   * rest (`newTeachers` / `newStudents`). Null when the server does not say.
   */
  alreadyInList?: number | null
  newTeachers?: number | null
  newStudents?: number | null
  /**
   * Allocation lines (AI server): cells placed now (`assigned`; `newAssignments` on the Teachers
   * file's draft), cells that already had the line's teacher, and cells another teacher keeps
   * until the user chooses. With them, `assignments` (the Teachers file) is still the total.
   */
  assigned?: number | null
  newAssignments?: number | null
  alreadyAssigned?: number | null
  heldByOther?: number | null
  assignments?: number | null
}

/**
 * Another step's rows in an onboarding upload, kept for that step as a finished draft
 * (`onboarding-carry`): that step opens it by `draftId` instead of reading the file again.
 */
export interface DataUploadCarried {
  draftId: string
  records: number
}

/** The drafts an upload carried, by the step they are for. */
export type DataUploadCarriedDrafts = Partial<
  Record<'allocation' | 'students' | 'teachers', DataUploadCarried>
>

/** The `title` of every carried draft (`GET data-upload/drafts?title=`). */
export const DATA_UPLOAD_CARRY_TITLE = 'onboarding-carry'

export interface DataUploadReport {
  /** Rows found in the upload; null on passthrough. */
  read: number | null
  /** Rows written into the template. */
  written: number
  /** Students only: saved students carried over next to the new ones. */
  kept: number
  skipped: DataUploadSkippedRow[]
  needsReview: DataUploadReviewRow[]
  /**
   * The files that could not be read, when others could — the failures of `sources`, for a
   * caller that would rather show one warning line than walk every file.
   */
  failedFiles: DataUploadSource[]
  /** Every written row's class, by id. Empty from builds that predate it. */
  references: DataUploadReference[]
  /** One question per written class name that matches several classes. */
  classQuestions: DataUploadClassQuestion[]
  /**
   * Allocation, a re-upload (AI server): lines whose class subject another teacher has now. Not
   * written: the page asks whether to replace that teacher. Absent from other builds.
   */
  heldByOther?: DataUploadHeldCell[]
}

/** A class subject a file gives one teacher while another teacher has it (`report.heldByOther`). */
export interface DataUploadHeldCell {
  subjectId: number
  teacherId: number
  holderId: number
  teacher: string | null
  holder: string | null
  class: string | null
  subject: string | null
}

const normalizeHeldCells = (value: unknown): DataUploadHeldCell[] =>
  toArray<unknown>(value)
    .map(asRecord)
    .filter((cell): cell is Record<string, unknown> => cell !== null)
    .map((cell) => ({
      subjectId: Number(cell.subjectId),
      teacherId: Number(cell.teacherId),
      holderId: Number(cell.holderId),
      teacher: toText(cell.teacher) || null,
      holder: toText(cell.holder) || null,
      class: toText(cell.class) || null,
      subject: toText(cell.subject) || null,
    }))
    .filter((cell) => cell.subjectId > 0 && cell.teacherId > 0)

export interface DataUploadFileResult {
  fileName?: string
  status?: 'ok' | 'no_relevant_data' | 'error' | string
  /** `template`, `deterministic` (read exactly, no AI), `ai_text`, `ai_ocr`, `ai_vision`. */
  route?: string
  reason?: string
  error?: {code?: string; message?: string} | null
  /** The reader was cut off (or returned far fewer rows than it counted): rows may be missing. */
  incomplete?: boolean
  /**
   * The screen whose list this file is (`teachers`, `students`, `allocation`), when it was set
   * aside here; `ignore` when the user chose not to read it.
   */
  assignedTo?: string
  /** `file` when the file itself said whose list it is; absent when the user answered. */
  assignedBy?: 'file' | string
  /**
   * What the file gave: rows read, and records taken from it (lines as written: one line can fill
   * several classes). A record two files both hold is merged and counted in each.
   * Allocation, AI server: `assignments`, the class-subject cells the file fills on the grid (a
   * cell an earlier file already fills is not counted again, so the files' add up to the upload's),
   * split into `newAssignments`, `alreadyAssigned` (its own teacher has it) and `heldByOther`.
   */
  counts?: {
    rows?: number
    records?: number
    assignments?: number
    newAssignments?: number
    alreadyAssigned?: number
    heldByOther?: number
  }
}

/**
 * What one uploaded file did, beside the merged list it fed into. Several files are read
 * together and their rows merged into one list, so `files[0]` is the merged result and this is
 * the per-file breakdown — including the ones that could not be read.
 */
export interface DataUploadSource {
  fileName?: string
  /** `error` also when every row of the file was left out (with `reason` + `message`). */
  status?: 'ok' | 'error' | string
  /** Rows the file contributed. */
  rows?: number
  /** Records actually kept from the file (teachers on Add Teachers), not rows read. */
  records?: number
  /** How many of those records need a look (`needsReview`); 0 for a clean read. */
  needsReview?: number
  /** Read, but some rows were left out (`status` stays `ok`). */
  partial?: boolean
  /** Rows left out of the file. */
  leftOutCount?: number
  /** The file was read as a photo or scan. */
  readFromPicture?: boolean
  /** On an error: false when sending the same file again cannot help (damaged, wrong type…). */
  retryable?: boolean
  /** The file's rows that were dropped, with why (`leftOut: [{label, code, row, message}]`). */
  leftOut?: DataUploadSkippedRow[]
  /** `table`, `text`, `ocr`, `vision`. */
  source?: string
  /** Failures only: the data-upload code and the sentence to show. */
  reason?: string
  message?: string
  /** The screen the user gave this file to ('ignore' when they chose not to read it). */
  assignedTo?: string
  /** `file` when the file itself said whose list it is; absent when the user answered. */
  assignedBy?: 'file' | string
}

/**
 * One sheet of a workbook that has to be assigned before anything can be read. Asked about only
 * when two or more sheets in a file carry a name column under a header every screen shares, so
 * the file itself cannot say whose list is whose.
 */
export interface DataUploadSheet {
  /** `"<fileName>::<sheet>"`. Send this back verbatim — it tells two `Sheet1`s apart. */
  key: string
  fileName?: string
  sheet?: string
  headers?: string[]
  rows?: number
  /** A few rows as they were read, for the user to recognise the list by. */
  sample?: string[][]
}

/** A screen a sheet may be assigned to, as the server names it. */
export interface DataUploadSheetChoice {
  key: string
  title: string
}

/** The question a call asks instead of answering: "whose list is each of these sheets?" */
export interface DataUploadAssignmentQuestion {
  sheets: DataUploadSheet[]
  choices: DataUploadSheetChoice[]
}

/**
 * A column the server could not place for sure (`columnQuestions[]`): `unsure` when the model
 * guessed its field below 80% (the guess is already applied to the rows), `unmapped` when no
 * field took it (its data is dropped). The rows are read either way; answering improves the
 * next read, and the answer is saved for this school and screen.
 */
export interface DataUploadColumnQuestion {
  /** As the file writes it; also the answer's key. */
  header: string
  key: string
  reason: 'unsure' | 'unmapped' | string
  guess: {field: string; confidence: number | null} | null
  samples: string[]
  /** One question per header, however many sheets or files have it. */
  appearsIn: Array<{fileName?: string; sheet?: string; column?: string}>
}

/** Every field of the screen, then "ignore" (`columnChoices[]`). */
export interface DataUploadColumnChoice {
  key: string
  title: string
}

/** "Which field is this column?": the questions of a read and the fields they may answer with. */
export interface DataUploadColumnQuestions {
  questions: DataUploadColumnQuestion[]
  choices: DataUploadColumnChoice[]
}

/** The user's answer: the column header as the file writes it -> a `columnChoices` key. */
export type DataUploadColumnAssignments = Record<string, string>

/** Assignment value for a column the user skipped: not read, and not asked about again. */
export const IGNORE_COLUMN = 'ignore'

/**
 * A subject the file writes under a name the school does not use — `Maths` where the school
 * picked `Mathematics`. Fuzzy matching handles spellings of one word, not a word and its
 * shorthand, so what is left over is proposed against the school's own subject list.
 */
export interface DataUploadSubjectMapping {
  /** The name as the file wrote it. */
  written: string
  /** A subject the school actually has; nothing else can be proposed. */
  subject: string
  /** 0..1. Anything below `SUBJECT_MAPPING_CONFIRM_THRESHOLD` is never ticked by default. */
  confidence?: number | null
  /** Why it was proposed ("usual short form"), shown as a tooltip. */
  reason?: string | null
  /** `applied` only: `alias` when it came from what this school confirmed earlier. */
  source?: string | null
}

/** `subjectMappings` of an extraction: what to ask, what was already known, what is left over. */
export interface DataUploadSubjectMappings {
  /** Proposed, NOT applied. Nothing changes until the user confirms. */
  suggestions: DataUploadSubjectMapping[]
  /** Already resolved from a mapping this school confirmed before — information, not a question. */
  applied: DataUploadSubjectMapping[]
  /** Names nothing could be proposed for; the user fixes the file or adds the subject. */
  unresolved: string[]
}

/** At or above this, a proposal is ticked by default; below it the user has to tick it. */
export const SUBJECT_MAPPING_CONFIRM_THRESHOLD = 0.8

/** A branch a sheet's students may belong to (`branchQuestions[].branches[]`). */
export interface DataUploadBranchChoice {
  branchId: number
  branch: string
}

/**
 * "Which branch are these students in?" (`branchQuestions[]`): asked once per sheet whose classes
 * exist in more than one branch, when neither a Branch column, the sheet's name nor an earlier
 * answer settled it. A single-branch school never gets one.
 */
export interface DataUploadBranchQuestion {
  /** `"<fileName>::<sheet>"`. The answer is sent under this exact key. */
  key: string
  fileName?: string
  sheet?: string
  /** Rows in this sheet that need a branch. */
  rows?: number
  /** `[name, class as written]`, up to 3. */
  sample: string[][]
  branches: DataUploadBranchChoice[]
  /**
   * Who the rows are, for the wording: `teacher` (Teachers step), `student` (Students step,
   * students prefill) or `allocation`. Missing from older backends and drafts: read as students.
   */
  rowNoun?: DataUploadBranchRowNoun
}

/** `branchQuestions[].rowNoun`: who a sheet's rows are. */
export type DataUploadBranchRowNoun = 'student' | 'teacher' | 'allocation'

const BRANCH_ROW_NOUNS: readonly string[] = ['student', 'teacher', 'allocation']

/** Branch answer for a sheet holding students of several branches: rows keep their class picker. */
export const MIXED_BRANCH = 'mixed'

/**
 * The user's answer: branch question key -> a `branchId`, or `MIXED_BRANCH`. Not saved per school
 * (the branch is a fact about this upload), so it goes with every later read of the same files.
 */
export type DataUploadBranchAssignments = Record<string, number | typeof MIXED_BRANCH>

/** Why a "Which classes?" line is asked (`allocationQuestions[].subjects[].issue`). */
export type DataUploadAllocationIssue =
  | 'NO_CLASS_WRITTEN'
  | 'SUBJECT_NOT_IN_SCHOOL'
  | 'CLASS_UNCLEAR'
  | (string & {})

/** One subject of a teacher asked about in `allocationQuestions[].subjects[]`. */
export interface DataUploadAllocationSubject {
  /** As the file wrote it ("Pak Std"). */
  written: string
  /** The school's subject it matched; null when it has none by that name (nothing to pick). */
  subject: string | null
  /** The class as the file wrote it ("Grade 1"); [] when it wrote none. Says why it is asked. */
  classes: string[]
  /**
   * The classes offering that subject: those the written class could mean ("Grade 1": 1-A and
   * 1-B), or, with no class written, every one in every branch and curriculum.
   */
  options: DataUploadClassOption[]
  /**
   * True when `options` are the classes the file itself names (they start picked); false when the
   * file names nothing usable and `options` is every class that teaches the subject (nothing is
   * picked). False when an older server leaves it out.
   */
  fromFile: boolean
  /**
   * Why the line is asked (extract-teachers): `NO_CLASS_WRITTEN` (the file names no class),
   * `SUBJECT_NOT_IN_SCHOOL` (nothing to pick: shown flagged), `CLASS_UNCLEAR` (a class no single
   * class takes). Absent from older servers.
   */
  issue?: DataUploadAllocationIssue
  /** The server's sentence for `issue`, about this line. */
  issueMessage?: string
  /**
   * The classes among `options` the file's words name, to start ticked even with `fromFile`
   * false: "both sections" on a five-section grade is A and B; "A, B, White, D, E" with no White
   * is the four that exist. Absent (older servers, or nothing named): `fromFile` decides.
   */
  picked?: number[]
}

/**
 * "Which classes does this teacher teach?" (`allocationQuestions[]`): the file gives a teacher's
 * subjects but no class (Teachers step, Teacher Allocation), or a class no single class takes —
 * a grade without its section, one name in two boards (Teacher Allocation). One entry per
 * teacher; a teacher whose file already places every line is never asked.
 */
export interface DataUploadAllocationQuestion {
  key: string
  teacher: string
  employeeId: string | null
  subjects: DataUploadAllocationSubject[]
  /** The files this teacher's lines are in (`fileNames`), when the server says; [] otherwise. */
  fileNames: string[]
}

/**
 * The answer for one teacher + subject (`allocationAssignments[]`). An empty `classIds` is an
 * answer too: that subject is not allocated, and it is not asked about again.
 */
export interface DataUploadAllocationAssignment {
  teacher: string
  employeeId: string | null
  subject: string
  classIds: number[]
}

/** The key one teacher + subject answer is known by (a later read must not ask it again). */
export const allocationAnswerKey = (
  answer: Pick<DataUploadAllocationAssignment, 'teacher' | 'employeeId' | 'subject'>
): string =>
  `${
    String(answer.employeeId ?? '')
      .trim()
      .toLowerCase() || String(answer.teacher).trim().toLowerCase()
  }::${String(answer.subject).trim().toLowerCase()}`

/** The user's answer: sheet key -> a `choices` key, or `IGNORE_SHEET`. */
export type DataUploadSheetAssignments = Record<string, string>

/** Assignment value for a sheet the user does not want read at all. */
export const IGNORE_SHEET = 'ignore'

/**
 * The user's answers to a draft's questions, kept on the draft (`PUT data-upload/drafts/:id/
 * answers`, `POST …/applied`) so a step resumed after a logout does not ask them again. The
 * template call uses the stored branch and allocation answers when it is sent none, and its JSON
 * carries them back (`answers`). Each key sent replaces the stored one whole.
 */
export interface DataUploadDraftAnswers {
  sheetAssignments?: DataUploadSheetAssignments
  columnAssignments?: DataUploadColumnAssignments
  branchAssignments?: DataUploadBranchAssignments
  allocationAssignments?: DataUploadAllocationAssignment[]
  teacherMatches?: DataUploadTeacherMatch[]
}

/** What a screen needs from a conversion, whichever endpoint produced it. */
export interface DataUploadConversion {
  passthrough: boolean
  template: DataUploadTemplate | null
  report: DataUploadReport
  files: DataUploadFileResult[]
  draftId: string | null
  /** The server's plain summary ("Read 52 rows. 50 teachers are ready…"), when it sent one. */
  message?: string | null
  counts?: DataUploadCounts | null
  /** One entry per uploaded file; `files[0]` is the merged result of all of them. */
  sources: DataUploadSource[]
  /**
   * Set when nothing was read because a workbook holds several unlabelled lists of people. No
   * template exists yet: ask, then post the same files again with the answer.
   */
  assignmentQuestion: DataUploadAssignmentQuestion | null
  /** Columns the server could not place for sure; null when there is nothing to ask. */
  columnQuestions: DataUploadColumnQuestions | null
  /** Subjects the file writes under a name of the school's own; null when there is nothing to ask. */
  subjectMappings: DataUploadSubjectMappings | null
  /** "Which branch are these students in?", one per sheet; empty when there is nothing to ask. */
  branchQuestions: DataUploadBranchQuestion[]
  /**
   * Allocation lines with no class, or a class no single class takes (the Allocation prefill
   * draft's template); empty when there is nothing to ask.
   */
  allocationQuestions: DataUploadAllocationQuestion[]
  /**
   * Teachers the upload names that the school has not saved (`newPeople.teachers`): offered to
   * the user to save; nothing is created until they say so.
   */
  newTeachers: DataUploadNewTeacher[]
  /**
   * Students the upload names (`newPeople.students`): a Teacher Allocation upload that turned
   * out to be a students list. Offered to the user to save on the Students step.
   */
  newStudents: DataUploadNewStudent[]
  /** Another step's rows in the same file, kept as drafts for that step (see DataUploadCarried). */
  carried: DataUploadCarriedDrafts | null
  /** A draft's template: the answers stored on the draft, when it has any. */
  answers?: DataUploadDraftAnswers | null
}

export interface DataUploadConfig {
  maxFileBytes: number
  maxFiles: number
  /**
   * Size cap for all the files of one selection together (`limits.maxTotalBytes` / `maxTotalMb`).
   * Absent when the server sends none: screens then fall back to `maxFileBytes * maxFiles`.
   */
  maxTotalBytes?: number
  /** Lower-cased extensions without the dot. */
  formats: string[]
}

// --- Limits & formats -------------------------------------------------------------------

/**
 * Used until `GET data-upload/use-cases` answers, and when it cannot be reached. Files travel in
 * 512 KB pieces (common/helpers/chunkedUpload), so nginx's body cap no longer sets the limit;
 * the server's own limits do, and this mirrors them: 10 files, 10 MB each, 50 MB for one upload
 * (the limits agreed in the 11 Oct review; node-api `limits.maxFileBytes` / `maxTotalBytes`).
 */
export const DEFAULT_DATA_UPLOAD_CONFIG: DataUploadConfig = {
  maxFileBytes: 10 * 1024 * 1024,
  maxFiles: 10,
  maxTotalBytes: 50 * 1024 * 1024,
  formats: [
    'xlsx',
    'csv',
    'txt',
    'ods',
    'docx',
    'odt',
    'pdf',
    'png',
    'jpg',
    'jpeg',
    'webp',
    'heic',
    'heif',
  ],
}

/** Formats the browser-side parsers read themselves; everything else needs a conversion. */
const SPREADSHEET_FORMATS: readonly string[] = ['xlsx', 'csv']
const IMAGE_FORMATS: readonly string[] = ['png', 'jpg', 'jpeg', 'webp', 'heic', 'heif']

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : null

const firstPositiveNumber = (...values: unknown[]): number | null => {
  for (const value of values) {
    const n = Number(value)
    if (Number.isFinite(n) && n > 0) return n
  }
  return null
}

/** Lower-cased extension of a file name, without the dot ("" when there is none). */
export const dataUploadFileExtension = (name: string): string => {
  const idx = name.lastIndexOf('.')
  return idx >= 0 ? name.slice(idx + 1).toLowerCase() : ''
}

export const isSpreadsheetUpload = (file: Pick<File, 'name'>): boolean =>
  SPREADSHEET_FORMATS.includes(dataUploadFileExtension(file.name))

export const isImageUpload = (file: Pick<File, 'name'>): boolean =>
  IMAGE_FORMATS.includes(dataUploadFileExtension(file.name))

/**
 * Limits and accepted formats out of the `data-upload/use-cases` response. The limits are
 * never hardcoded by the screens; anything the response does not state keeps its default.
 *
 * @param response The API response (envelope or bare `result`).
 * @param useCaseKey Narrows the formats to that use case's `acceptedFormats` when it lists any.
 */
export const parseDataUploadConfig = (
  response: unknown,
  useCaseKey?: DataUploadUseCaseKey
): DataUploadConfig => {
  const envelope = asRecord(response)
  const result = asRecord(envelope?.result) ?? envelope
  const limits = asRecord(result?.limits)

  const megabytes = firstPositiveNumber(limits?.maxFileMb, limits?.maxFileMB, limits?.perFileMb)
  const maxFileBytes =
    firstPositiveNumber(
      limits?.maxFileBytes,
      limits?.maxFileSizeBytes,
      limits?.perFileBytes,
      limits?.maxFileSize,
      limits?.fileSizeBytes
    ) ?? (megabytes ? megabytes * 1024 * 1024 : null)
  const maxFiles = firstPositiveNumber(
    limits?.maxFiles,
    limits?.maxFilesPerRequest,
    limits?.filesPerRequest
  )
  const totalMegabytes = firstPositiveNumber(limits?.maxTotalMb, limits?.maxTotalMB, limits?.totalMb)
  const maxTotalBytes =
    firstPositiveNumber(limits?.maxTotalBytes, limits?.totalBytes, limits?.maxRequestBytes) ??
    (totalMegabytes ? totalMegabytes * 1024 * 1024 : null)

  const rawUseCases = result?.useCases ?? result?.use_cases ?? result?.items
  const useCases: unknown[] = Array.isArray(rawUseCases)
    ? rawUseCases
    : Array.isArray(result)
      ? (result as unknown[])
      : Object.entries(asRecord(rawUseCases) ?? {}).map(([key, value]) => ({
          key,
          ...(asRecord(value) ?? {}),
        }))
  const useCase = useCases
    .map(asRecord)
    .find((entry) => entry && (entry.key ?? entry.useCaseKey) === useCaseKey)
  const rawFormats = useCase?.acceptedFormats ?? limits?.acceptedFormats ?? limits?.formats
  const formats = Array.isArray(rawFormats)
    ? rawFormats
        .map((format) =>
          String(format ?? '')
            .trim()
            .toLowerCase()
            .replace(/^\./, '')
        )
        .filter(Boolean)
    : []

  return {
    maxFileBytes: maxFileBytes ?? DEFAULT_DATA_UPLOAD_CONFIG.maxFileBytes,
    maxFiles: maxFiles ?? DEFAULT_DATA_UPLOAD_CONFIG.maxFiles,
    maxTotalBytes: maxTotalBytes ?? DEFAULT_DATA_UPLOAD_CONFIG.maxTotalBytes,
    formats: formats.length > 0 ? formats : DEFAULT_DATA_UPLOAD_CONFIG.formats,
  }
}

/** `accept` attribute value for a file input. */
export const dataUploadAcceptAttribute = (formats: readonly string[]): string =>
  formats.map((format) => `.${format}`).join(',')

/** "7 MB" style label for the limits copy. */
export const formatDataUploadBytes = (bytes: number): string => {
  const mb = bytes / (1024 * 1024)
  return `${Number.isInteger(mb) ? mb : mb.toFixed(1)} MB`
}

/**
 * Pre-flight check of one file, run before anything is sent. Returns the backend's own code for
 * the refusal (so both share one translation), or null when the file may go.
 */
export const validateDataUploadFile = (
  file: Pick<File, 'name' | 'size'>,
  config: DataUploadConfig = DEFAULT_DATA_UPLOAD_CONFIG
): 'FILE_TYPE_NOT_ALLOWED' | 'TOO_LARGE' | 'EMPTY' | null => {
  if (!config.formats.includes(dataUploadFileExtension(file.name))) return 'FILE_TYPE_NOT_ALLOWED'
  if (file.size === 0) return 'EMPTY'
  if (file.size > config.maxFileBytes) return 'TOO_LARGE'
  return null
}

// --- Request ----------------------------------------------------------------------------

/**
 * FormData for `POST data-upload/convert`. Non-file fields are strings. `branch-id` travels as
 * a header (added by the saga), which the backend uses to tell same-named classes apart.
 */
export const buildDataUploadConvertForm = (
  files: readonly File[],
  options: {
    useCaseKey: DataUploadUseCaseKey
    flavour?: DataUploadFlavour
    /** Students, onboarding flavour: ask for the layout with a separate Curriculum column. */
    curriculumColumn?: boolean
    /** A file already sent in pieces: sent instead of `files`. */
    uploadId?: string | null
    /** Several files already sent in pieces, read as one upload: sent instead of `files`. */
    uploadIds?: readonly string[] | null
    /** Answers to an earlier call's "whose list is this sheet?" question. */
    sheetAssignments?: DataUploadSheetAssignments | null
    /** Answers to an earlier call's "which field is this column?" questions. */
    columnAssignments?: DataUploadColumnAssignments | null
    /** Answers to an earlier call's "which branch are these students in?" questions. */
    branchAssignments?: DataUploadBranchAssignments | null
  }
): FormData => {
  const form = new FormData()
  if (options.uploadIds && options.uploadIds.length > 0) {
    form.append('uploadIds', JSON.stringify(options.uploadIds))
  } else if (options.uploadId) form.append('uploadId', options.uploadId)
  // Several files are read together and their rows merged into one list, so the same teacher in
  // two files is one row. `files` is the field for one file as well as for many.
  else files.forEach((file) => form.append('files', file, file.name))
  form.append('useCaseKey', options.useCaseKey)
  if (options.flavour) form.append('flavour', options.flavour)
  if (options.curriculumColumn) form.append('curriculum_column', 'true')
  appendSheetAssignments(form, options.sheetAssignments)
  appendColumnAssignments(form, options.columnAssignments)
  appendBranchAssignments(form, options.branchAssignments)
  return form
}

/**
 * The branch questions still open once `answers` are taken into account: a sheet already answered
 * (with a branch or "mixed") is never asked about again, even if a read brings it back.
 */
export const withoutAnsweredBranches = (
  questions: readonly DataUploadBranchQuestion[] | null | undefined,
  answers: DataUploadBranchAssignments | null | undefined
): DataUploadBranchQuestion[] =>
  (questions ?? []).filter((question) => !(question.key in (answers ?? {})))

/**
 * The branch questions nobody needs to be asked: a sheet with one branch to choose from goes to
 * that branch. Sent like any other answer; the dialog only opens for the rest.
 */
export const singleBranchAnswers = (
  questions: readonly DataUploadBranchQuestion[] | null | undefined
): DataUploadBranchAssignments =>
  Object.fromEntries(
    (questions ?? [])
      .filter((question) => question.branches.length === 1)
      .map((question) => [question.key, question.branches[0].branchId])
  )

/**
 * The allocation questions still open once `answers` are taken into account: a teacher +
 * subject already answered (even with no class) is not asked again. A teacher with none left
 * goes; a subject the school doesn't have stays, as information.
 */
export const withoutAnsweredAllocations = (
  questions: readonly DataUploadAllocationQuestion[] | null | undefined,
  answers: readonly DataUploadAllocationAssignment[] | null | undefined
): DataUploadAllocationQuestion[] => {
  const answered = new Set((answers ?? []).map(allocationAnswerKey))
  const open = (questions ?? [])
    .map((question) => ({
      ...question,
      subjects: question.subjects.filter(
        (subject) =>
          !subject.subject ||
          !answered.has(
            allocationAnswerKey({
              teacher: question.teacher,
              employeeId: question.employeeId,
              subject: subject.subject,
            })
          )
      ),
    }))
  // Asked only while some line has something to pick. Then a teacher whose lines are all
  // subjects the school doesn't have is shown too, flagged (not hidden); on its own it never opens
  // the dialog (nothing to answer, it would come back after every Confirm).
  const answerable = open.some((question) => question.subjects.some((subject) => subject.subject))
  return answerable ? open.filter((question) => question.subjects.length > 0) : []
}

/**
 * "Which classes?" skipped: every open teacher + subject answered with no class, exactly as
 * asked. The server then leaves those lines out ("don't allocate this") and imports the rest,
 * instead of the whole file waiting on them. A subject the school doesn't have has nothing to
 * answer.
 */
export const skippedAllocationAnswers = (
  questions: readonly DataUploadAllocationQuestion[] | null | undefined
): DataUploadAllocationAssignment[] =>
  (questions ?? []).flatMap((question) =>
    question.subjects
      .filter((entry) => entry.subject)
      .map((entry) => ({
        teacher: question.teacher,
        employeeId: question.employeeId,
        subject: entry.subject as string,
        classIds: [],
      }))
  )

/**
 * A branch question about a whole file (`key` "<fileName>::", no sheet): the server found no
 * branch in the file's name, a Branch column, a sheet name or a title line, and asks once for the
 * file, not once per sheet.
 */
export const isFileBranchQuestion = (question: Pick<DataUploadBranchQuestion, 'key' | 'sheet'>) =>
  !String(question.sheet ?? '').trim() && String(question.key).endsWith('::')

/**
 * What a read's open questions are about, as the pending-questions bar says it: "3 teachers need
 * classes · 2 subjects need confirming". Teachers, not their lines (a line means nothing to the
 * user): one per teacher asked, with the subjects in brackets when there are more of them. Then
 * columns to match, lists that need a branch (the ones not answered yet), subjects to confirm.
 * Null when nothing is open (the caller says "Waiting for your answer" then).
 */
export const pendingQuestionsText = (
  formatMessage: FormatMessage,
  questions: {
    allocations?: readonly DataUploadAllocationQuestion[] | null
    columns?: DataUploadColumnQuestions | null
    branches?: readonly DataUploadBranchQuestion[] | null
    /** Branch answers already given (a dialog reopened to change them): not open. */
    answeredBranches?: Readonly<Record<string, unknown>> | null
    subjects?: DataUploadSubjectMappings | null
  }
): string | null => {
  const asked = (questions.allocations ?? []).filter((question) =>
    question.subjects.some((entry) => entry.subject)
  )
  const subjects = asked.reduce(
    (sum, question) => sum + question.subjects.filter((entry) => entry.subject).length,
    0
  )
  const columns = questions.columns?.questions.length ?? 0
  const openBranches = (questions.branches ?? []).filter(
    (question) => !(question.key in (questions.answeredBranches ?? {}))
  )
  // A whole file asked about is said as a file, not a list.
  const files = openBranches.filter(isFileBranchQuestion).length
  const branches = openBranches.length - files
  const proposals = questions.subjects?.suggestions.length ?? 0
  const parts = [
    asked.length > 0
      ? formatMessage(
          {
            id:
              subjects > asked.length
                ? 'DATA_UPLOAD.QUESTIONS.TEACHERS_SUBJECTS_NEED_CLASSES'
                : 'DATA_UPLOAD.QUESTIONS.TEACHERS_NEED_CLASSES',
          },
          {teachers: asked.length, subjects}
        )
      : null,
    columns > 0
      ? formatMessage({id: 'DATA_UPLOAD.QUESTIONS.COLUMNS_NEED_MATCHING'}, {count: columns})
      : null,
    files > 0
      ? formatMessage({id: 'DATA_UPLOAD.QUESTIONS.FILES_NEED_BRANCH'}, {count: files})
      : null,
    branches > 0
      ? formatMessage({id: 'DATA_UPLOAD.QUESTIONS.LISTS_NEED_BRANCH'}, {count: branches})
      : null,
    proposals > 0
      ? formatMessage({id: 'DATA_UPLOAD.QUESTIONS.SUBJECTS_NEED_CONFIRMING'}, {count: proposals})
      : null,
  ].filter((part): part is string => Boolean(part))
  return parts.length > 0 ? parts.join(' · ') : null
}

/**
 * The answers to allocation questions, as the field `onboarding/extract-teachers` and
 * `csv-import/upload` read them from (a JSON string). Sent with every later read, like the
 * other answers.
 */
export const appendAllocationAssignments = (
  form: FormData,
  assignments: readonly DataUploadAllocationAssignment[] | null | undefined
): void => {
  if (assignments && assignments.length > 0) {
    form.append('allocationAssignments', JSON.stringify(assignments))
  }
}

/**
 * A teacher an upload names that the school has not saved (`newPeople.teachers[]`). Nothing is
 * created until the user approves it by `key`.
 */
export interface DataUploadNewTeacher {
  /** Sent back as is in `approvedNewTeachers`; never rebuilt from the name. */
  key: string
  name: string
  employeeId: string | null
  email: string | null
  /** Allocation lines that wait on this teacher. */
  lines: number
  /** "Grade 5 A / Maths": a few of those lines, to recognise the teacher by. */
  samples: string[]
  /** `allocation_lines` (dropped lines) or `teacher_list` (another screen's list). */
  from?: string
  /**
   * Up to 3 saved teachers that are probably this same person, best first. Nothing is matched
   * automatically: the user picks one ("Match to existing"), adds the teacher, or skips.
   */
  suggestions: DataUploadTeacherSuggestion[]
}

/** A saved teacher an unknown name probably is (`newPeople.teachers[].suggestions[]`). */
export interface DataUploadTeacherSuggestion {
  teacherId: number
  name: string
  employeeId: string | null
  /** 0..1. */
  score: number | null
  /** same_employee_id, spelling, extra_word, initials, word_order or spacing. */
  why: string | null
}

/**
 * "This unknown name is that saved teacher" (`teacherMatches[]`): its lines are placed on the
 * saved teacher and it drops out of `newPeople`. Travels like `allocationAssignments`.
 */
export interface DataUploadTeacherMatch {
  name: string
  teacherId: number
}

/** The matches as the field the server reads (a JSON array), sent with every re-post. */
export const appendTeacherMatches = (
  form: FormData,
  matches: readonly DataUploadTeacherMatch[] | null | undefined
): void => {
  if (matches && matches.length > 0) form.append('teacherMatches', JSON.stringify(matches))
}

/** `newPeople.teachers` of an answer (or of a failed call's `conversion`); [] when none. */
export const normalizeNewTeachers = (newPeople: unknown): DataUploadNewTeacher[] =>
  toArray<unknown>(asRecord(newPeople)?.teachers)
    .map(asRecord)
    .filter((teacher): teacher is Record<string, unknown> => teacher !== null)
    .map((teacher) => ({
      key: toText(teacher.key),
      name: toText(teacher.name),
      employeeId: toText(teacher.employeeId) || null,
      email: toText(teacher.email) || null,
      lines: toCount(teacher.lines) ?? 0,
      samples: toArray<unknown>(teacher.samples).map(toText).filter(Boolean),
      from: toText(teacher.from) || undefined,
      suggestions: toArray<unknown>(teacher.suggestions)
        .map(asRecord)
        .filter((entry): entry is Record<string, unknown> => entry !== null)
        .map((entry) => ({
          teacherId: toCount(entry.teacherId) ?? 0,
          name: toText(entry.name),
          employeeId: toText(entry.employeeId) || null,
          score: typeof entry.score === 'number' ? entry.score : null,
          why: toText(entry.why) || null,
        }))
        .filter((entry) => entry.teacherId > 0 && entry.name)
        .slice(0, 3),
    }))
    .filter((teacher) => teacher.key && teacher.name)

/**
 * A student an upload names (`newPeople.students[]`), e.g. a students list uploaded on Teacher
 * Allocation. Nothing is created until the user saves them on the Students step.
 */
export interface DataUploadNewStudent {
  name: string
  registrationNumber: string | null
  class: string | null
  classId: number | null
  section: string | null
  subjects: string[]
}

/** `newPeople.students` of an answer (or of a failed call's `conversion`); [] when none. */
export const normalizeNewStudents = (newPeople: unknown): DataUploadNewStudent[] =>
  toArray<unknown>(asRecord(newPeople)?.students)
    .map(asRecord)
    .filter((student): student is Record<string, unknown> => student !== null)
    .map((student) => ({
      name: toText(student.name),
      registrationNumber: toText(student.registrationNumber) || null,
      class: toText(student.class) || null,
      classId: toCount(student.classId) || null,
      section: toText(student.section) || null,
      subjects: toArray<unknown>(student.subjects)
        .map((subject) => toText(asRecord(subject)?.name ?? subject))
        .filter(Boolean),
    }))
    .filter((student) => student.name)

/**
 * The new teachers the user said to save (their `key`s), as `csv-import/upload` reads them: a
 * JSON array, sent with every re-post of the same file like the other answers.
 */
export const appendApprovedNewTeachers = (
  form: FormData,
  keys: readonly string[] | null | undefined
): void => {
  if (keys && keys.length > 0) form.append('approvedNewTeachers', JSON.stringify(keys))
}

/**
 * The answers to branch questions, as the field the server reads them from. Not saved per
 * school, so they go with every later read of the same files, like the sheet answers.
 */
export const appendBranchAssignments = (
  form: FormData,
  assignments: DataUploadBranchAssignments | null | undefined
): void => {
  if (assignments && Object.keys(assignments).length > 0) {
    form.append('branchAssignments', JSON.stringify(assignments))
  }
}

/**
 * The subject proposals still open once `confirmed` (written names, lower-cased) are taken into
 * account: a mapping the user already confirmed is not asked again even if a slow or rejected
 * alias brings it back as a suggestion.
 */
export const withoutConfirmedSubjects = (
  m: DataUploadSubjectMappings | null,
  confirmed?: ReadonlySet<string>
): DataUploadSubjectMappings | null =>
  m && confirmed?.size
    ? {...m, suggestions: m.suggestions.filter((s) => !confirmed.has(s.written.toLowerCase()))}
    : m

/**
 * `m` with the subject mappings the user already confirmed (by lower-cased written name) listed
 * under `applied`, so a dialog reopened for a new question shows them as done, not blank.
 */
export const withConfirmedSubjectsApplied = (
  m: DataUploadSubjectMappings | null,
  confirmed?: ReadonlyMap<string, DataUploadSubjectMapping>
): DataUploadSubjectMappings | null => {
  if (!m || !confirmed?.size) return m
  const shown = new Set(m.applied.map((mapping) => mapping.written.toLowerCase()))
  const done = Array.from(confirmed.entries())
    .filter(([written]) => !shown.has(written))
    .map(([, mapping]) => mapping)
  return done.length > 0 ? {...m, applied: [...m.applied, ...done]} : m
}

/**
 * The column questions still open once `answers` are taken into account: a header already
 * answered is never asked again here, even if a read brings it back, so the dialog cannot loop.
 * Null when none is left.
 */
export const withoutAnsweredColumns = (
  questions: DataUploadColumnQuestions | null | undefined,
  answers: DataUploadColumnAssignments | null | undefined
): DataUploadColumnQuestions | null => {
  if (!questions) return null
  const answered = new Set(Object.keys(answers ?? {}).map((header) => header.trim().toLowerCase()))
  const open = questions.questions.filter(
    (question) => !answered.has(question.header.trim().toLowerCase())
  )
  return open.length > 0 ? {...questions, questions: open} : null
}

/**
 * The answers to column questions, as the field the server reads them from. Sent with the next
 * read of the same file(s); the server saves each answer for this school and screen as it uses it.
 */
export const appendColumnAssignments = (
  form: FormData,
  assignments: DataUploadColumnAssignments | null | undefined
): void => {
  if (assignments && Object.keys(assignments).length > 0) {
    form.append('columnAssignments', JSON.stringify(assignments))
  }
}

/**
 * The answers to a sheet question, as the field the server reads them from. Assignments are not
 * remembered between uploads, so they go with every re-post of the same files.
 */
export const appendSheetAssignments = (
  form: FormData,
  assignments: DataUploadSheetAssignments | null | undefined
): void => {
  if (assignments && Object.keys(assignments).length > 0) {
    form.append('sheetAssignments', JSON.stringify(assignments))
  }
}

// --- Response ---------------------------------------------------------------------------

const toArray = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : [])

const toCountMap = (value: unknown): Record<string, number> => {
  const out: Record<string, number> = {}
  Object.entries(asRecord(value) ?? {}).forEach(([code, count]) => {
    const n = Number(count)
    if (Number.isFinite(n) && n > 0) out[code] = n
  })
  return out
}

const toCount = (value: unknown): number | null => {
  const n = Number(value)
  return value !== null && value !== undefined && Number.isFinite(n) ? n : null
}

const toMessage = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null

/** `counts` of a data-upload answer, or null when it has none. */
export const normalizeDataUploadCounts = (value: unknown): DataUploadCounts | null => {
  const counts = asRecord(value)
  if (!counts) return null
  return {
    read: toCount(counts.read),
    kept: toCount(counts.kept ?? counts.written),
    // Older builds sent no `ready`; `kept` is then the closest thing the caller can say.
    ready: toCount(counts.ready ?? counts.kept ?? counts.written),
    leftOut: toCountMap(counts.leftOut),
    needValues: toCountMap(counts.needValues),
    alreadyInSchool: toCount(counts.alreadyInSchool),
    alreadyInList: toCount(counts.alreadyInList),
    newTeachers: toCount(counts.newTeachers),
    newStudents: toCount(counts.newStudents),
    assigned: toCount(counts.assigned),
    newAssignments: toCount(counts.newAssignments),
    alreadyAssigned: toCount(counts.alreadyAssigned),
    heldByOther: toCount(counts.heldByOther),
    assignments: toCount(counts.assignments),
    newPeople: asRecord(counts.newPeople)
      ? {
          teachers: toCount(asRecord(counts.newPeople)?.teachers) ?? 0,
          students: toCount(asRecord(counts.newPeople)?.students) ?? 0,
        }
      : null,
  }
}

/** `carried` of an answer: `{allocation|students: {draftId, records}}`; null when none. */
export const normalizeCarriedDrafts = (value: unknown): DataUploadCarriedDrafts | null => {
  const data = asRecord(value)
  if (!data) return null
  const out: DataUploadCarriedDrafts = {}
  // `teachers`: an Allocation file's teachers, for Add Teachers (AI server).
  ;(['allocation', 'students', 'teachers'] as const).forEach((key) => {
    const entry = asRecord(data[key])
    const draftId = toText(entry?.draftId)
    if (draftId) out[key] = {draftId, records: toCount(entry?.records) ?? 0}
  })
  return Object.keys(out).length > 0 ? out : null
}

const toText = (value: unknown): string => String(value ?? '').trim()

/** `sources[]` of an answer: one entry per uploaded file, however many were sent. */
export const normalizeDataUploadSources = (value: unknown): DataUploadSource[] =>
  toArray<unknown>(value)
    .map(asRecord)
    .filter((source): source is Record<string, unknown> => source !== null)
    .map((source) => ({
      fileName: toText(source.fileName) || undefined,
      status: toText(source.status) || undefined,
      rows: toCount(source.rows) ?? undefined,
      records: toCount(source.records) ?? undefined,
      needsReview: toCount(source.needsReview) ?? undefined,
      partial: typeof source.partial === 'boolean' ? source.partial : undefined,
      leftOutCount: toCount(source.leftOutCount) ?? undefined,
      readFromPicture:
        typeof source.readFromPicture === 'boolean' ? source.readFromPicture : undefined,
      retryable: typeof source.retryable === 'boolean' ? source.retryable : undefined,
      leftOut: toArray<unknown>(source.leftOut)
        .map(asRecord)
        .filter((row): row is Record<string, unknown> => row !== null)
        .map((row) => ({
          label: toText(row.label) || undefined,
          code: toText(row.code) || undefined,
          reason: toText(row.reason) || toText(row.code) || undefined,
          message: toText(row.message) || undefined,
          recordId: toText(row.row) || toText(row.recordId) || undefined,
        })),
      source: toText(source.source) || undefined,
      reason: toText(source.reason) || undefined,
      message: toText(source.message) || undefined,
      assignedTo: toText(source.assignedTo) || undefined,
      assignedBy: toText(source.assignedBy) || undefined,
    }))

/**
 * The "which field is this column?" questions of a read (`columnQuestions` + `columnChoices`),
 * or null when it asks none: the server leaves both keys out then. A question with no header is
 * dropped, since its header is the answer's key.
 */
export const normalizeColumnQuestions = (value: unknown): DataUploadColumnQuestions | null => {
  const data = asRecord(value)
  const questions = toArray<unknown>(data?.columnQuestions)
    .map(asRecord)
    .filter((entry): entry is Record<string, unknown> => Boolean(entry && toText(entry.header)))
    .map((entry) => {
      const guess = asRecord(entry.guess)
      return {
        header: toText(entry.header),
        key: toText(entry.key) || toText(entry.header).toLowerCase(),
        reason: toText(entry.reason),
        guess:
          guess && toText(guess.field)
            ? {field: toText(guess.field), confidence: toCount(guess.confidence)}
            : null,
        samples: toArray<unknown>(entry.samples).map(toText).filter(Boolean),
        appearsIn: toArray<unknown>(entry.appearsIn)
          .map(asRecord)
          .filter((place): place is Record<string, unknown> => place !== null)
          .map((place) => ({
            fileName: toText(place.fileName) || undefined,
            sheet: toText(place.sheet) || undefined,
            column: toText(place.column) || undefined,
          })),
      }
    })
  if (questions.length === 0) return null
  const choices = toArray<unknown>(data?.columnChoices)
    .map(asRecord)
    .filter((choice): choice is Record<string, unknown> => Boolean(choice && toText(choice.key)))
    .map((choice) => ({key: toText(choice.key), title: toText(choice.title) || toText(choice.key)}))
  return {questions, choices}
}

/**
 * The "whose list is this sheet?" question an answer asks instead of answering, or null when it
 * asks none. A sheet with no `key` is dropped: the key is what the answer is posted under, and
 * a made-up one would be discarded server-side anyway.
 */
export const normalizeAssignmentQuestion = (
  value: unknown
): DataUploadAssignmentQuestion | null => {
  const data = asRecord(value)
  if (!data || data.needsAssignment !== true) return null
  // `data-upload/convert` lists the sheets at the top, keyed. The conversion a failed
  // `csv-import/upload` carries (NEEDS_ASSIGNMENT) has them only per file, unkeyed: the key is
  // then built the same way the server builds it, `<fileName>::<sheet>`.
  const topLevel = toArray<unknown>(data.sheets)
  const listed =
    topLevel.length > 0
      ? topLevel
      : toArray<unknown>(data.files).flatMap((entry) => {
          const file = asRecord(entry)
          return toArray<unknown>(file?.sheets).map((raw) => {
            const sheet = asRecord(raw) ?? {}
            const fileName = toText(sheet.fileName) || toText(file?.fileName)
            return {
              ...sheet,
              fileName,
              key: toText(sheet.key) || `${fileName}::${toText(sheet.sheet)}`,
            }
          })
        })
  const sheets = listed
    .map(asRecord)
    .filter((sheet): sheet is Record<string, unknown> => Boolean(sheet && toText(sheet.key)))
    .map((sheet) => ({
      key: toText(sheet.key),
      fileName: toText(sheet.fileName) || undefined,
      sheet: toText(sheet.sheet) || undefined,
      headers: toArray<unknown>(sheet.headers).map(toText),
      rows: toCount(sheet.rows) ?? undefined,
      sample: toArray<unknown>(sheet.sample).map((row) => toArray<unknown>(row).map(toText)),
    }))
  if (sheets.length === 0) return null
  const choices = toArray<unknown>(data.choices)
    .map(asRecord)
    .filter((choice): choice is Record<string, unknown> => Boolean(choice && toText(choice.key)))
    .map((choice) => ({key: toText(choice.key), title: toText(choice.title) || toText(choice.key)}))
  return {sheets, choices}
}

/**
 * The "which branch are these students in?" questions of a read (`branchQuestions`), one per
 * sheet. A question with no key, or with no branch to pick, is dropped: the key is what the
 * answer is posted under.
 */
export const normalizeBranchQuestions = (value: unknown): DataUploadBranchQuestion[] =>
  toArray<unknown>(asRecord(value)?.branchQuestions)
    .map(asRecord)
    .filter((entry): entry is Record<string, unknown> => Boolean(entry && toText(entry.key)))
    .map((entry) => ({
      key: toText(entry.key),
      fileName: toText(entry.fileName) || undefined,
      sheet: toText(entry.sheet) || undefined,
      rows: toCount(entry.rows) ?? undefined,
      sample: toArray<unknown>(entry.sample).map((row) => toArray<unknown>(row).map(toText)),
      branches: toArray<unknown>(entry.branches)
        .map(asRecord)
        .map((branch) => ({branchId: toId(branch?.branchId), branch: toText(branch?.branch)}))
        .filter((branch): branch is DataUploadBranchChoice => branch.branchId !== null),
      // An unknown noun is dropped: the wording then falls back to students, as without one.
      ...(BRANCH_ROW_NOUNS.includes(toText(entry.rowNoun).toLowerCase())
        ? {rowNoun: toText(entry.rowNoun).toLowerCase() as DataUploadBranchRowNoun}
        : {}),
    }))
    .filter((question) => question.branches.length > 0)

/**
 * The "which classes does this teacher teach?" questions (`allocationQuestions`): a Teachers-step
 * read, or a Teacher Allocation upload's lines that no single class takes. A teacher with no
 * subject to ask about is dropped.
 */
export const normalizeAllocationQuestions = (value: unknown): DataUploadAllocationQuestion[] =>
  toArray<unknown>(asRecord(value)?.allocationQuestions)
    .map(asRecord)
    .filter((entry): entry is Record<string, unknown> => Boolean(entry && toText(entry.teacher)))
    .map((entry) => ({
      key: toText(entry.key) || toText(entry.employeeId) || toText(entry.teacher),
      teacher: toText(entry.teacher),
      employeeId: toText(entry.employeeId) || null,
      fileNames: Array.from(
        new Set(
          [
            // extract-teachers names the one file a teacher's lines are in (`fileName`).
            entry.fileName,
            ...toArray<unknown>(entry.fileNames),
            ...toArray<unknown>(entry.sources).map((source) => asRecord(source)?.fileName),
          ]
            .map(toText)
            .filter(Boolean)
        )
      ),
      subjects: toArray<unknown>(entry.subjects)
        .map(asRecord)
        .filter((subject): subject is Record<string, unknown> =>
          Boolean(subject && (toText(subject.written) || toText(subject.subject)))
        )
        .map((subject) => ({
          written: toText(subject.written) || toText(subject.subject),
          // Not one of the school's subjects: nothing to pick, whatever name came with it (shown
          // flagged, never answered).
          subject:
            toText(subject.issue) === 'SUBJECT_NOT_IN_SCHOOL'
              ? null
              : toText(subject.subject) || null,
          classes: toArray<unknown>(subject.classes).map(toText).filter(Boolean),
          options: toClassOptions(subject.options),
          fromFile: subject.fromFile === true || subject.fromFile === 'true',
          ...(toText(subject.issue) ? {issue: toText(subject.issue)} : {}),
          ...(toText(subject.issueMessage) ? {issueMessage: toText(subject.issueMessage)} : {}),
          // None named (or an empty list) is no `picked`: `fromFile` decides, as before.
          ...(pickedClassIds(subject.picked).length > 0
            ? {picked: pickedClassIds(subject.picked)}
            : {}),
        })),
    }))
    .filter((question) => question.subjects.length > 0)

/** `allocationQuestions[].subjects[].picked`: the class ids, each once; [] for anything else. */
const pickedClassIds = (value: unknown): number[] =>
  Array.from(
    new Set(
      toArray<unknown>(value)
        .map((id) => Number(id))
        .filter((id) => Number.isInteger(id) && id > 0)
    )
  )

const toSubjectMapping = (value: unknown): DataUploadSubjectMapping | null => {
  const mapping = asRecord(value)
  const written = toText(mapping?.written)
  const subject = toText(mapping?.subject)
  if (!written || !subject) return null
  return {
    written,
    subject,
    confidence: toCount(mapping?.confidence),
    reason: toText(mapping?.reason) || null,
    source: toText(mapping?.source) || null,
  }
}

/** `subjectMappings` of an answer, or null when there is nothing to show or confirm. */
export const normalizeSubjectMappings = (value: unknown): DataUploadSubjectMappings | null => {
  const data = asRecord(value)
  if (!data) return null
  const list = (raw: unknown) =>
    toArray<unknown>(raw)
      .map(toSubjectMapping)
      .filter((mapping): mapping is DataUploadSubjectMapping => mapping !== null)
  const suggestions = list(data.suggestions)
  const applied = list(data.applied)
  const unresolved = toArray<unknown>(data.unresolved).map(toText).filter(Boolean)
  if (suggestions.length === 0 && applied.length === 0 && unresolved.length === 0) return null
  return {suggestions, applied, unresolved}
}

/** Proposals ticked when the confirm step opens: the ones the model was confident about. */
export const defaultConfirmedSubjectMappings = (
  mappings: DataUploadSubjectMappings | null | undefined
): DataUploadSubjectMapping[] =>
  (mappings?.suggestions ?? []).filter(
    (mapping) => (mapping.confidence ?? 0) >= SUBJECT_MAPPING_CONFIRM_THRESHOLD
  )

/** Body of `POST data-upload/subject-aliases` for the mappings the user ticked. */
export const subjectAliasesBody = (confirmed: readonly DataUploadSubjectMapping[]) => ({
  mappings: confirmed.map((mapping) => ({
    written: mapping.written,
    subject: mapping.subject,
    ...(mapping.confidence != null ? {confidence: mapping.confidence} : {}),
  })),
})

/**
 * Mappings `data-upload/subject-aliases` refused (onto a subject the school does not have). That
 * should not happen when only the server's own suggestions are sent back, so it is worth a word.
 */
export const rejectedSubjectAliasCount = (response: unknown): number => {
  const rejected = asRecord(asRecord(response)?.result ?? response)?.rejected
  return Array.isArray(rejected) ? rejected.length : 0
}

const toTemplate = (value: unknown): DataUploadTemplate | null => {
  const template = asRecord(value)
  const base64 = typeof template?.base64 === 'string' ? template.base64 : ''
  if (!template || !base64) return null
  return {
    key: typeof template.key === 'string' ? template.key : undefined,
    fileName: String(template.fileName ?? 'Template.xlsx'),
    contentType: String(
      template.contentType ?? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ),
    base64,
  }
}

/** A positive integer id, or null. */
const toId = (value: unknown): number | null => {
  const n = Number(value)
  return value !== null && value !== '' && Number.isInteger(n) && n > 0 ? n : null
}

/** One `options.class[]` / `classQuestions[].options[]` entry; null without a class id. */
const toClassOption = (value: unknown): DataUploadClassOption | null => {
  const option = asRecord(value)
  const classId = toId(option?.classId)
  if (!option || !classId) return null
  const name = toText(option.name)
  const schoolDisplayName = toText(option.schoolDisplayName)
  return {
    classId,
    name,
    displayName: toText(option.displayName) || name,
    ...(schoolDisplayName ? {schoolDisplayName} : {}),
    levelId: toId(option.levelId),
    level: toText(option.level),
    curriculumKey: toText(option.curriculumKey) || null,
    curriculum: toText(option.curriculum),
    curriculumShortCode: toText(option.curriculumShortCode),
    branchId: toId(option.branchId),
    branch: toText(option.branch),
  }
}

const toClassOptions = (value: unknown): DataUploadClassOption[] =>
  toArray<unknown>(value)
    .map(toClassOption)
    .filter((option): option is DataUploadClassOption => option !== null)

/** `needsReview[]` entry, with `options.class` typed when the server sent it. */
/**
 * A review reason as the portal shows it. The server adds "; the level or branch decides which"
 * after "matches 2 classes"; levels and branches are not something the user picks here, so that
 * part is dropped.
 */
const displayReviewReason = (reason: string): string =>
  reason.replace(/;\s*the level or branch decides which\.?/i, '').trim()

const normalizeReviewRow = (row: DataUploadReviewRow): DataUploadReviewRow => {
  const options = toClassOptions(asRecord(row?.options)?.class)
  const normalized: DataUploadReviewRow = {...row}
  if (row?.reasons) {
    normalized.reasons = Object.fromEntries(
      Object.entries(row.reasons).map(([field, reason]) => [
        field,
        displayReviewReason(String(reason ?? '')),
      ])
    )
  }
  if (options.length > 0) normalized.options = {class: options}
  else delete normalized.options
  return normalized
}

/** `report.references`: each written row's class by id. */
export const normalizeDataUploadReferences = (value: unknown): DataUploadReference[] =>
  toArray<unknown>(value)
    .map(asRecord)
    .filter((entry): entry is Record<string, unknown> => entry !== null)
    .map((entry) => ({
      recordId: toText(entry.recordId),
      label: toText(entry.label),
      registrationNumber: toText(entry.registrationNumber),
      classId: toId(entry.classId),
      levelId: toId(entry.levelId),
      branchId: toId(entry.branchId),
      subjectIds: toArray<unknown>(entry.subjectIds)
        .map(toId)
        .filter((id): id is number => id !== null),
    }))

/** `report.classQuestions`: one question per written class name that matches several classes. */
const normalizeClassQuestions = (value: unknown): DataUploadClassQuestion[] =>
  toArray<unknown>(value)
    .map(asRecord)
    .filter((entry): entry is Record<string, unknown> => entry !== null)
    .map((entry) => ({
      field: toText(entry.field) || 'class',
      written: toText(entry.written),
      labels: toArray<unknown>(entry.labels).map(toText),
      recordIds: toArray<unknown>(entry.recordIds).map(toText),
      options: toClassOptions(entry.options),
    }))

/**
 * Normalizes the `result` of `data-upload/convert`, or the `conversion` field that
 * `csv-import/upload` adds when it converted the file first. Null when `value` is neither.
 */
export const normalizeDataUploadConversion = (value: unknown): DataUploadConversion | null => {
  const data = asRecord(value)
  if (
    !data ||
    (!('report' in data) &&
      !('template' in data) &&
      !('passthrough' in data) &&
      // A call that only asks whose list each sheet is has none of the three: nothing was read.
      data.needsAssignment !== true)
  ) {
    return null
  }
  const report = asRecord(data.report)
  const read = report?.read
  return {
    passthrough: data.passthrough === true,
    template: toTemplate(data.template),
    report: {
      read: typeof read === 'number' ? read : null,
      written: Number(report?.written ?? 0) || 0,
      kept: Number(report?.kept ?? 0) || 0,
      skipped: toArray<DataUploadSkippedRow>(report?.skipped),
      needsReview: toArray<DataUploadReviewRow>(report?.needsReview).map(normalizeReviewRow),
      failedFiles: normalizeDataUploadSources(report?.failedFiles),
      references: normalizeDataUploadReferences(report?.references),
      classQuestions: normalizeClassQuestions(report?.classQuestions),
      ...(Array.isArray(report?.heldByOther)
        ? {heldByOther: normalizeHeldCells(report?.heldByOther)}
        : {}),
    },
    files: toArray<DataUploadFileResult>(data.files),
    draftId: typeof data.draftId === 'string' ? data.draftId : null,
    message: toMessage(data.message),
    counts: normalizeDataUploadCounts(data.counts),
    sources: normalizeDataUploadSources(data.sources),
    assignmentQuestion: normalizeAssignmentQuestion(data),
    columnQuestions: normalizeColumnQuestions(data),
    subjectMappings: normalizeSubjectMappings(data.subjectMappings),
    branchQuestions: normalizeBranchQuestions(data),
    allocationQuestions: normalizeAllocationQuestions(data),
    newTeachers: normalizeNewTeachers(data.newPeople),
    newStudents: normalizeNewStudents(data.newPeople),
    carried: normalizeCarriedDrafts(data.carried),
    answers: normalizeDataUploadDraftAnswers(data.answers),
  }
}

/** An answer field as stored: an object (or array), or the JSON string of one. */
const parsedAnswer = (value: unknown): unknown => {
  if (typeof value !== 'string') return value
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

/**
 * The answers a draft carries (`answers` of a template, `draft.answers`, or the answer of
 * `PUT …/answers`), keeping only the fields that hold something. Null when none does.
 */
export const normalizeDataUploadDraftAnswers = (value: unknown): DataUploadDraftAnswers | null => {
  const data = asRecord(parsedAnswer(value))
  if (!data) return null
  const answers: DataUploadDraftAnswers = {}
  const recordField = (key: 'sheetAssignments' | 'columnAssignments' | 'branchAssignments') => {
    const field = asRecord(parsedAnswer(data[key]))
    if (field && !Array.isArray(field) && Object.keys(field).length > 0) {
      ;(answers as Record<string, unknown>)[key] = field
    }
  }
  recordField('sheetAssignments')
  recordField('columnAssignments')
  recordField('branchAssignments')
  const allocations = parsedAnswer(data.allocationAssignments)
  if (Array.isArray(allocations) && allocations.length > 0) {
    answers.allocationAssignments = allocations.filter(
      (entry): entry is DataUploadAllocationAssignment =>
        Boolean(entry) && typeof entry === 'object' && Array.isArray(entry.classIds)
    )
  }
  const matches = parsedAnswer(data.teacherMatches)
  if (Array.isArray(matches) && matches.length > 0) {
    answers.teacherMatches = matches.filter(
      (entry): entry is DataUploadTeacherMatch =>
        Boolean(entry) && typeof entry === 'object' && Number(entry.teacherId) > 0
    )
  }
  return Object.keys(answers).length > 0 ? answers : null
}

/**
 * The file (or a sheet of it) also holds teacher allocations, which only Teacher Allocation
 * imports: a file or sheet given to `allocation`, or new teachers named by allocation lines.
 */
export const dataUploadHasAllocations = (
  conversion: Pick<DataUploadConversion, 'files' | 'sources' | 'newTeachers'>
): boolean =>
  conversion.files.some((file) => file.assignedTo === 'allocation') ||
  conversion.sources.some((source) => source.assignedTo === 'allocation') ||
  conversion.newTeachers.some((teacher) => teacher.from === 'allocation_lines')

/**
 * The files that could not be read, when others could. `sources` carries every file; older
 * answers only sent the failures, under `report.failedFiles`.
 */
export const failedDataUploadSources = (
  conversion: Pick<DataUploadConversion, 'sources' | 'report'>
): DataUploadSource[] => {
  const failed = conversion.sources.filter((source) => source.status === 'error')
  return failed.length > 0 ? failed : conversion.report.failedFiles
}

/** True when at least one file was read by AI: those rows deserve a "please check" badge. */
export const wasReadByAi = (conversion: Pick<DataUploadConversion, 'files'>): boolean =>
  conversion.files.some((file) => String(file.route ?? '').startsWith('ai_'))

/** The workbook as a `File`, ready for whatever handles a template upload on that screen. */
export const dataUploadTemplateToFile = (template: DataUploadTemplate): File => {
  const bytes = Uint8Array.from(atob(template.base64), (c) => c.charCodeAt(0))
  return new File([bytes], template.fileName, {type: template.contentType})
}

/** Saves the filled template, so the user can fix it in Excel and re-upload it (no AI then). */
export const downloadDataUploadTemplate = (template: DataUploadTemplate): void => {
  const url = URL.createObjectURL(dataUploadTemplateToFile(template))
  const link = document.createElement('a')
  link.href = url
  link.download = template.fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// --- Errors -----------------------------------------------------------------------------

/**
 * Translated copy per backend code. Codes arrive in `result.code` (never in an `error` field),
 * or in `files[].error.code` for a single bad file.
 */
export const DATA_UPLOAD_ERROR_KEYS = {
  NO_FILES: 'DATA_UPLOAD.CONVERT.ERROR.NO_FILES',
  TOO_MANY_FILES: 'DATA_UPLOAD.CONVERT.ERROR.TOO_MANY_FILES',
  TOO_LARGE: 'DATA_UPLOAD.CONVERT.ERROR.TOO_LARGE',
  // The files of one upload together over `limits.maxTotalBytes` (413, with the sizes).
  TOTAL_TOO_LARGE: 'DATA_UPLOAD.CONVERT.ERROR.TOTAL_TOO_LARGE',
  FILE_TYPE_NOT_ALLOWED: 'DATA_UPLOAD.CONVERT.ERROR.FILE_TYPE_NOT_ALLOWED',
  FILE_SIGNATURE_MISMATCH: 'DATA_UPLOAD.CONVERT.ERROR.FILE_SIGNATURE_MISMATCH',
  // The file is damaged and cannot be opened (re-save it, or choose another file).
  FILE_DAMAGED: 'DATA_UPLOAD.CONVERT.ERROR.FILE_DAMAGED',
  ARCHIVE_UNSAFE: 'DATA_UPLOAD.CONVERT.ERROR.ARCHIVE_UNSAFE',
  EMPTY: 'DATA_UPLOAD.CONVERT.ERROR.EMPTY',
  TOO_MANY_PAGES: 'DATA_UPLOAD.CONVERT.ERROR.TOO_MANY_PAGES',
  PARSE_FAILED: 'DATA_UPLOAD.CONVERT.ERROR.PARSE_FAILED',
  NO_RELEVANT_DATA: 'DATA_UPLOAD.CONVERT.ERROR.NO_RELEVANT_DATA',
  AI_UNAVAILABLE: 'DATA_UPLOAD.CONVERT.ERROR.AI_UNAVAILABLE',
  AI_DISABLED: 'DATA_UPLOAD.CONVERT.ERROR.AI_UNAVAILABLE',
  AI_BUDGET_EXCEEDED: 'DATA_UPLOAD.CONVERT.ERROR.AI_BUDGET_EXCEEDED',
  USE_CASE_REQUIRED: 'DATA_UPLOAD.CONVERT.ERROR.USE_CASE_REQUIRED',
  USE_CASE_NOT_SUPPORTED: 'DATA_UPLOAD.CONVERT.ERROR.USE_CASE_NOT_SUPPORTED',
  NO_ROWS_CONVERTED: 'DATA_UPLOAD.CONVERT.ERROR.NO_ROWS_CONVERTED',
  // Allocation: every line names a teacher the school has not saved (`conversion.newPeople`).
  TEACHERS_NOT_IN_SCHOOL: 'DATA_UPLOAD.CONVERT.ERROR.TEACHERS_NOT_IN_SCHOOL',
  // Allocation: lines with no class, or a class with several sections (`allocationQuestions`).
  ALLOCATION_CLASSES_REQUIRED: 'DATA_UPLOAD.CONVERT.ERROR.ALLOCATION_CLASSES_REQUIRED',
  // Allocation: every line was skipped ("Which classes?" answered with none). Information, not a
  // failure: shown with `dataUploadErrorIsInfo`, in the server's own words when it sends them.
  ALL_LINES_SKIPPED: 'DATA_UPLOAD.CONVERT.ERROR.ALL_LINES_SKIPPED',
  FORBIDDEN: 'DATA_UPLOAD.CONVERT.ERROR.FORBIDDEN',
  DRAFT_NOT_FOUND: 'DATA_UPLOAD.CONVERT.ERROR.DRAFT_NOT_FOUND',
  JOB_RUNNING: 'DATA_UPLOAD.CONVERT.ERROR.JOB_RUNNING',
  VALIDATION_REQUIRED: 'DATA_UPLOAD.CONVERT.ERROR.VALIDATION_REQUIRED',
  // Row checks: a file's `reason` when every row failed one, or `skipped[].code`. Take {count}.
  NAME_NOT_ENGLISH: 'DATA_UPLOAD.CONVERT.ERROR.NAME_NOT_ENGLISH',
  NAME_HAS_DIGITS: 'DATA_UPLOAD.CONVERT.ERROR.NAME_HAS_DIGITS',
  NOT_A_NAME: 'DATA_UPLOAD.CONVERT.ERROR.NOT_A_NAME',
  NAME_SYMBOLS_ONLY: 'DATA_UPLOAD.CONVERT.ERROR.NAME_SYMBOLS_ONLY',
  NOT_A_RECORD: 'DATA_UPLOAD.CONVERT.ERROR.NOT_A_RECORD',
  // Chunked upload (common/helpers/chunkedUpload).
  UPLOAD_NOT_FOUND: 'DATA_UPLOAD.CONVERT.ERROR.UPLOAD_NOT_FOUND',
  UPLOAD_INCOMPLETE: 'DATA_UPLOAD.CONVERT.ERROR.UPLOAD_INCOMPLETE',
} satisfies Record<string, TranslationKey>

/** Codes that report what the user chose rather than a failure: shown as information. */
const DATA_UPLOAD_INFO_CODES = new Set<string>(['ALL_LINES_SKIPPED'])

/** The call "failed" with a code that is only information (e.g. every line skipped on purpose). */
export const dataUploadErrorIsInfo = (error: unknown): boolean =>
  DATA_UPLOAD_INFO_CODES.has(dataUploadErrorCode(error) ?? '')

/** Codes that describe rows rather than the file; their copy reads "{count} names are…". */
export const DATA_UPLOAD_ROW_CODES = [
  'NAME_NOT_ENGLISH',
  'NAME_HAS_DIGITS',
  'NOT_A_NAME',
  'NAME_SYMBOLS_ONLY',
  'NOT_A_RECORD',
] as const

export type DataUploadRowCode = (typeof DATA_UPLOAD_ROW_CODES)[number]

const isDataUploadRowCode = (code: string | null | undefined): code is DataUploadRowCode =>
  Boolean(code && (DATA_UPLOAD_ROW_CODES as readonly string[]).includes(code))

/** The same rules said about one left-out row (`report.skipped[]`). */
const DATA_UPLOAD_ROW_CODE_KEYS = {
  NAME_NOT_ENGLISH: 'DATA_UPLOAD.CONVERT.SKIP.NAME_NOT_ENGLISH',
  NAME_HAS_DIGITS: 'DATA_UPLOAD.CONVERT.SKIP.NAME_HAS_DIGITS',
  NOT_A_NAME: 'DATA_UPLOAD.CONVERT.SKIP.NOT_A_NAME',
  NAME_SYMBOLS_ONLY: 'DATA_UPLOAD.CONVERT.SKIP.NAME_SYMBOLS_ONLY',
  NOT_A_RECORD: 'DATA_UPLOAD.CONVERT.SKIP.NOT_A_RECORD',
} satisfies Record<DataUploadRowCode, TranslationKey>

export type DataUploadErrorCode = keyof typeof DATA_UPLOAD_ERROR_KEYS

/** `result.code` of a failed call (the saga hands the whole envelope to `onFailure`). */
export const dataUploadErrorCode = (error: unknown): string | null => {
  if (typeof error === 'string') return error || null
  const envelope = asRecord(error)
  const nested = asRecord(asRecord(envelope?.response)?.data)
  const result = asRecord(envelope?.result) ?? asRecord(nested?.result)
  const code = result?.code ?? envelope?.code
  return typeof code === 'string' && code ? code : null
}

/** `result.data` (or `result`) of a failed call: where `retryable` / `actualFormat` travel. */
const errorDetails = (error: unknown): Record<string, unknown> | null => {
  const envelope = asRecord(error)
  const result = asRecord(envelope?.result) ?? asRecord(asRecord(asRecord(envelope?.response)?.data)?.result)
  return asRecord(result?.data) ?? result
}

/**
 * Codes about the file's own content: sending the same bytes again gives the same answer, so
 * "Try again" is not offered (the user replaces the file instead).
 */
const NOT_RETRYABLE_CODES = new Set<string>([
  'FILE_DAMAGED',
  'FILE_SIGNATURE_MISMATCH',
  'FILE_TYPE_NOT_ALLOWED',
  'EMPTY',
  'PARSE_FAILED',
  'NO_RELEVANT_DATA',
  'ARCHIVE_UNSAFE',
  'TOO_LARGE',
  'TOO_MANY_PAGES',
  ...DATA_UPLOAD_ROW_CODES,
])

/**
 * Whether trying the same file again can help. The server says so (`retryable`, on the failed
 * source or in `result.data`); when it doesn't, false for the file-content codes above and true
 * for everything else (a dropped connection, the AI briefly unavailable…).
 */
export const dataUploadRetryable = (
  code: string | null | undefined,
  said?: boolean | null,
  error?: unknown
): boolean => {
  if (typeof said === 'boolean') return said
  const fromError = errorDetails(error)?.retryable ?? asRecord(asRecord(error)?.result)?.retryable
  if (typeof fromError === 'boolean') return fromError
  return !(code && NOT_RETRYABLE_CODES.has(code))
}

/** The file's real format when its extension lies (`FILE_SIGNATURE_MISMATCH` + `actualFormat`). */
export const dataUploadActualFormat = (error: unknown): string | null => {
  const format = errorDetails(error)?.actualFormat ?? asRecord(asRecord(error)?.result)?.actualFormat
  return typeof format === 'string' && format.trim() ? format.trim().toLowerCase() : null
}

/** HTTP status of a failed call: the envelope's `statusCode`, or a proxy error page's status. */
export const dataUploadErrorStatus = (error: unknown): number | null => {
  const envelope = asRecord(error)
  const status = Number(envelope?.statusCode ?? envelope?.status)
  return Number.isFinite(status) && status > 0 ? status : null
}

/**
 * The questions a failed `csv-import/upload` asks instead of reading (Teacher Allocation):
 * `NEEDS_ASSIGNMENT` — whose list each sheet is; `BRANCH_REQUIRED` — which branch a sheet's
 * allocations are in; `ALLOCATION_CLASSES_REQUIRED` — which classes lines with no class (or a
 * class with several sections) are in. Read from the raw `result.conversion`, which may carry
 * neither a report nor a template. The same file is posted again with the answers.
 */
export const dataUploadErrorQuestions = (
  error: unknown
): {
  sheets: DataUploadAssignmentQuestion | null
  branches: DataUploadBranchQuestion[]
  allocations: DataUploadAllocationQuestion[]
} => {
  const conversion = asRecord(asRecord(asRecord(error)?.result)?.conversion)
  const code = dataUploadErrorCode(error)
  return {
    sheets:
      normalizeAssignmentQuestion(conversion) ??
      (code === 'NEEDS_ASSIGNMENT'
        ? normalizeAssignmentQuestion({...(conversion ?? {}), needsAssignment: true})
        : null),
    branches: normalizeBranchQuestions(conversion),
    allocations: normalizeAllocationQuestions(conversion),
  }
}

/** The `conversion` that a failed `csv-import/upload` carries next to its code, if any. */
export const dataUploadErrorConversion = (error: unknown): DataUploadConversion | null => {
  const envelope = asRecord(error)
  const result = asRecord(envelope?.result)
  return normalizeDataUploadConversion(result?.conversion)
}

/** Translation key for a backend code; null when the code is unknown (show `message` then). */
export const dataUploadErrorKey = (code: string | null | undefined): TranslationKey | null =>
  code && code in DATA_UPLOAD_ERROR_KEYS
    ? DATA_UPLOAD_ERROR_KEYS[code as DataUploadErrorCode]
    : null

/**
 * The first per-file failure of a conversion that otherwise succeeded. One bad file does not
 * fail the request: the other files' rows still come back.
 */
export const firstDataUploadFileError = (
  conversion: Pick<DataUploadConversion, 'files'>
): {code: string; message?: string} | null => {
  for (const file of conversion.files) {
    // Another screen's list, set aside on purpose: not a failure of this read.
    if (file.status === 'no_relevant_data' && file.assignedTo) continue
    if (file.status === 'error' && file.error?.code) {
      return {code: file.error.code, message: file.error.message ?? undefined}
    }
    if (file.status === 'no_relevant_data') return {code: 'NO_RELEVANT_DATA'}
  }
  return null
}

/** Why a row was left out: translated meaning per `report.skipped[].reason`. */
export const DATA_UPLOAD_SKIP_REASON_KEYS = {
  MISSING_REQUIRED: 'DATA_UPLOAD.CONVERT.SKIP.MISSING_REQUIRED',
  MISSING_REGISTRATION: 'DATA_UPLOAD.CONVERT.SKIP.MISSING_REGISTRATION',
  UNRESOLVED: 'DATA_UPLOAD.CONVERT.SKIP.UNRESOLVED',
  NO_SLOT: 'DATA_UPLOAD.CONVERT.SKIP.NO_SLOT',
  CONFLICT: 'DATA_UPLOAD.CONVERT.SKIP.CONFLICT',
  DUPLICATE: 'DATA_UPLOAD.CONVERT.SKIP.DUPLICATE',
  ALREADY_EXISTS: 'DATA_UPLOAD.CONVERT.SKIP.ALREADY_EXISTS',
  INVALID: 'DATA_UPLOAD.CONVERT.SKIP.INVALID',
  EXCLUDED: 'DATA_UPLOAD.CONVERT.SKIP.EXCLUDED',
  // Allocation: a line whose "Which classes?" question was skipped (no class chosen).
  CLASSES_NOT_CHOSEN: 'DATA_UPLOAD.CONVERT.SKIP.CLASSES_NOT_CHOSEN',
  // Allocation: the line's teacher is not saved in the school (yet).
  TEACHER_NOT_FOUND: 'DATA_UPLOAD.CONVERT.SKIP.TEACHER_NOT_FOUND',
  // Allocation: the line's class was deleted since (a curriculum, level or class change).
  CLASS_NOT_FOUND: 'DATA_UPLOAD.CONVERT.SKIP.CLASS_NOT_FOUND',
} satisfies Record<string, TranslationKey>

export const dataUploadSkipReasonKey = (reason: string | undefined): TranslationKey | null =>
  reason && reason in DATA_UPLOAD_SKIP_REASON_KEYS
    ? DATA_UPLOAD_SKIP_REASON_KEYS[reason as keyof typeof DATA_UPLOAD_SKIP_REASON_KEYS]
    : null

/**
 * Translation for one left-out row: its own `code` first (Students: `NAME_NOT_ENGLISH` under
 * reason `INVALID`; Teachers: the row code itself), then its `reason`.
 */
export const dataUploadSkippedRowKey = (
  row: Pick<DataUploadSkippedRow, 'code' | 'reason'>
): TranslationKey | null => {
  if (isDataUploadRowCode(row.code)) return DATA_UPLOAD_ROW_CODE_KEYS[row.code]
  if (isDataUploadRowCode(row.reason)) return DATA_UPLOAD_ROW_CODE_KEYS[row.reason]
  // A skipped "Which classes?" line comes as a code or a reason: either way, said the same.
  if (row.code === 'CLASSES_NOT_CHOSEN') return DATA_UPLOAD_SKIP_REASON_KEYS.CLASSES_NOT_CHOSEN
  return dataUploadSkipReasonKey(row.reason)
}

type FormatMessage = (descriptor: {id: TranslationKey}, values?: Record<string, any>) => string

/**
 * User-facing copy for a failed upload or conversion. The code is mapped to translated copy;
 * the backend `message` is only a fallback for a code this build does not know. Without a code
 * the HTTP status decides: a proxy's 413 is "too large", a 408/502/504 "took too long". A call
 * that got no answer at all (the connection dropped) says so.
 *
 * @param error Either a code, or whatever the saga handed to `onFailure`.
 * @param context.count For a row code (`NAME_NOT_ENGLISH`…): how many rows it hit.
 */
export const dataUploadErrorMessage = (
  formatMessage: FormatMessage,
  error: unknown,
  context: {
    config?: DataUploadConfig
    useCaseKey?: DataUploadUseCaseKey
    fileName?: string
    count?: number
  } = {}
): string => {
  const envelope = asRecord(error)
  const code = dataUploadErrorCode(error)
  const config = context.config ?? DEFAULT_DATA_UPLOAD_CONFIG
  const values = {
    n: config.maxFiles,
    size: formatDataUploadBytes(config.maxFileBytes),
    useCase: context.useCaseKey ?? 'other',
    count: context.count ?? 1,
  }

  const extension = context.fileName ? dataUploadFileExtension(context.fileName) : ''
  if (code === 'FILE_TYPE_NOT_ALLOWED' && (extension === 'xls' || extension === 'doc')) {
    return formatMessage({id: 'DATA_UPLOAD.CONVERT.ERROR.LEGACY_FORMAT'})
  }
  // The extension says one thing, the content another: name the real format when the server knows.
  if (code === 'FILE_SIGNATURE_MISMATCH') {
    const actualFormat = dataUploadActualFormat(error)
    if (actualFormat) {
      return formatMessage({id: 'DATA_UPLOAD.CONVERT.ERROR.FILE_SIGNATURE_MISMATCH_AS'}, {actualFormat})
    }
  }
  if (code === 'TOTAL_TOO_LARGE') {
    // "These files add up to 230 MB; the limit is 200 MB for one upload." The sizes come with
    // the refusal (`result.totalBytes`, `result.maxTotalBytes`); the limit falls back to ours.
    const result = asRecord(envelope?.result) ?? envelope
    const totalBytes = Number(result?.totalBytes)
    const limitBytes = Number(result?.maxTotalBytes) || config.maxTotalBytes
    const limit = limitBytes ? formatDataUploadBytes(limitBytes) : ''
    return Number.isFinite(totalBytes) && totalBytes > 0
      ? formatMessage(
          {id: 'DATA_UPLOAD.CONVERT.ERROR.TOTAL_TOO_LARGE'},
          {total: formatDataUploadBytes(totalBytes), limit}
        )
      : formatMessage({id: 'DATA_UPLOAD.CONVERT.ERROR.TOTAL_TOO_LARGE_LIMIT'}, {limit})
  }
  // An information code is said in the server's words (it names what was skipped); ours is the
  // fallback.
  if (code && DATA_UPLOAD_INFO_CODES.has(code)) {
    const said = toMessage(asRecord(envelope?.result)?.message) ?? toMessage(envelope?.message)
    if (said) return said
  }
  const key = dataUploadErrorKey(code)
  if (key) return formatMessage({id: key}, values)

  const status = dataUploadErrorStatus(error)
  if (status === 404) return formatMessage({id: 'DATA_UPLOAD.CONVERT.ERROR.NOT_AVAILABLE'})
  if (status === 403) return formatMessage({id: 'DATA_UPLOAD.CONVERT.ERROR.FORBIDDEN'})
  if (status === 413) return formatMessage({id: 'DATA_UPLOAD.CONVERT.ERROR.TOO_LARGE'}, values)
  if (status === 408 || status === 502 || status === 504) {
    return formatMessage({id: 'DATA_UPLOAD.CONVERT.ERROR.TIMED_OUT'})
  }
  // No answer at all: fetch itself failed (connection dropped, or a proxy page without CORS).
  if (error instanceof Error || (!code && status === null)) {
    return formatMessage({id: 'DATA_UPLOAD.CONVERT.ERROR.CONNECTION_LOST'})
  }
  const message = toMessage(asRecord(envelope?.result)?.message) ?? toMessage(envelope?.message)
  return message || formatMessage({id: 'GENERAL.SOMETHING_WENT_WRONG'})
}

/**
 * A translated summary built from `counts`: "Read 52 rows. 50 teachers are ready. 1 line is a
 * heading or note, not a person." Null when a left-out code has no translation here; the caller
 * shows the server's own `message` then, which is always complete.
 */
export const dataUploadCountsSummary = (
  formatMessage: FormatMessage,
  counts: DataUploadCounts | null | undefined,
  useCaseKey: DataUploadUseCaseKey
): string | null => {
  if (!counts || counts.read === null || counts.kept === null) return null
  const lines = [
    formatMessage(
      {id: 'DATA_UPLOAD.CONVERT.COUNTS.READ_KEPT'},
      // "N are ready" means the rows that can be saved, not the rows that went into the file:
      // saying `kept` here is what put "5 students are ready" above "4 need a class".
      {read: counts.read, kept: counts.ready ?? counts.kept, useCase: useCaseKey}
    ),
  ]
  for (const [code, count] of Object.entries(counts.leftOut)) {
    if (!isDataUploadRowCode(code)) return null
    lines.push(formatMessage({id: DATA_UPLOAD_ERROR_KEYS[code]}, {count}))
  }
  return lines.join(' ')
}

// --- Drafts: the Teachers file carried forward to Students / Allocation -----------------

/** Title `onboarding/prefill` gives its drafts. */
export const ONBOARDING_PREFILL_DRAFT_TITLE = 'onboarding-prefill'

export interface DataUploadDraftSummary {
  id: string
  /** `queued`, `processing`, `done`, `failed`, `cancelled`; null when the list left it out. */
  jobStatus: string | null
}

const RUNNING_JOB_STATUSES = [
  'queued',
  'pending',
  'processing',
  'running',
  'in_progress',
  'started',
]
const DONE_JOB_STATUSES = ['done', 'completed', 'complete', 'succeeded', 'success', 'finished']
const FAILED_JOB_STATUSES = ['failed', 'error', 'cancelled', 'canceled']

/**
 * A job's status as `queued` / `processing` / `done` / `failed` / `cancelled`, or null when
 * there is none. Synonyms are folded in; anything else (a bare job id where a job object was
 * expected) is null, never mistaken for a status.
 *
 * `interrupted` (node-api reports it even for a job still queued, with `finishedAt: null`)
 * counts as still running until the job has a `finishedAt`; after that it failed.
 */
const toJobStatus = (value: unknown): string | null => {
  const raw = asRecord(value)?.status ?? asRecord(value)?.state ?? value
  if (typeof raw !== 'string') return null
  const status = raw
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
  if (status === 'interrupted') return asRecord(value)?.finishedAt ? 'failed' : 'processing'
  if (RUNNING_JOB_STATUSES.includes(status)) return status === 'queued' ? 'queued' : 'processing'
  if (DONE_JOB_STATUSES.includes(status)) return 'done'
  if (FAILED_JOB_STATUSES.includes(status))
    return status === 'failed' || status === 'error' ? 'failed' : 'cancelled'
  return null
}

/** The newest draft of `GET data-upload/drafts` (`result.drafts[0]`), or null. */
export const newestDataUploadDraft = (response: unknown): DataUploadDraftSummary | null => {
  const envelope = asRecord(response)
  const result = envelope?.result ?? envelope
  const drafts = Array.isArray(result) ? result : toArray<unknown>(asRecord(result)?.drafts)
  const draft = asRecord(drafts[0])
  const id = draft?.id ?? draft?.draftId
  if (typeof id !== 'string' && typeof id !== 'number') return null
  return {id: String(id), jobStatus: toJobStatus(draft?.job ?? draft?.jobStatus)}
}

/** The drafts of `GET data-upload/drafts`, newest first: id and record count. */
export const dataUploadDraftList = (
  response: unknown
): Array<{id: string; useCaseKey: string; records: number}> => {
  const envelope = asRecord(response)
  const result = envelope?.result ?? envelope
  const drafts = Array.isArray(result) ? result : toArray<unknown>(asRecord(result)?.drafts)
  return drafts
    .map(asRecord)
    .filter((draft): draft is Record<string, unknown> => Boolean(draft && toText(draft.id)))
    .map((draft) => ({
      id: toText(draft.id),
      useCaseKey: toText(draft.useCaseKey),
      records: toCount(asRecord(draft.counts)?.records) ?? 0,
    }))
}

/** `job.status` of `GET data-upload/drafts/:id/job`. */
export const dataUploadJobStatus = (response: unknown): string | null => {
  const envelope = asRecord(response)
  const result = asRecord(envelope?.result) ?? envelope
  return toJobStatus(result?.job ?? result?.status)
}

/** `job.progress.percent` (0..100) of `GET data-upload/drafts/:id/job`, or null. */
export const dataUploadJobPercent = (response: unknown): number | null => {
  const envelope = asRecord(response)
  const result = asRecord(envelope?.result) ?? envelope
  const job = asRecord(result?.job) ?? result
  const percent = Number(asRecord(job?.progress)?.percent)
  return Number.isFinite(percent) ? Math.min(100, Math.max(0, Math.round(percent))) : null
}

/** True while a draft's job is still reading: its template call answers 409 then. */
export const isDataUploadJobRunning = (status: string | null | undefined): boolean =>
  status === 'queued' || status === 'processing'
